"use client";

import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import { mutedPanel } from "./section";

interface Quote {
  quote: string;
  name: string;
  title: string;
  avatar: string;
}

const ADVANCE_MS = 8000;

export function QuoteCarousel({ quotes }: { quotes: Quote[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (
      paused ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const id = window.setTimeout(() => {
      setIndex((i) => (i + 1) % quotes.length);
    }, ADVANCE_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [index, paused, quotes.length]);

  const go = (step: number) => {
    setIndex((i) => (i + step + quotes.length) % quotes.length);
  };

  return (
    <div
      className={cn(
        mutedPanel,
        "flex w-full flex-col items-center gap-8 px-5 py-10 text-center sm:gap-10 sm:px-12 sm:py-16",
      )}
      onMouseEnter={() => {
        setPaused(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
      }}
      onFocus={() => {
        setPaused(true);
      }}
      onBlur={() => {
        setPaused(false);
      }}
    >
      <div className="grid w-full max-w-3xl" aria-live="polite">
        {quotes.map((q, i) => (
          <figure
            key={q.name}
            aria-hidden={i !== index}
            className={cn(
              "col-start-1 row-start-1 flex flex-col items-center gap-6 transition-opacity duration-500 motion-reduce:transition-none",
              i === index ? "opacity-100" : "pointer-events-none opacity-0",
            )}
          >
            <blockquote className="text-marketing-text text-[19px] leading-[1.35] font-medium tracking-[-0.015em] text-balance sm:text-[28px] sm:leading-[1.3]">
              &ldquo;{q.quote}&rdquo;
            </blockquote>
            <figcaption className="flex items-center gap-3 text-left">
              <Image
                src={q.avatar}
                alt=""
                width={40}
                height={40}
                className="size-10 rounded-full object-cover"
              />
              <span className="flex flex-col">
                <span className="text-marketing-text text-sm font-medium">
                  {q.name}
                </span>
                <span className="text-marketing-text-muted text-sm">
                  {q.title}
                </span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Previous quote"
          onClick={() => {
            go(-1);
          }}
          className="text-marketing-text inline-flex size-9 items-center justify-center rounded-full bg-white transition-colors hover:bg-white/70"
        >
          <ArrowLeftIcon className="size-4" aria-hidden="true" />
        </button>
        <span className="text-marketing-text-muted w-12 text-center text-sm tabular-nums">
          {index + 1} / {quotes.length}
        </span>
        <button
          type="button"
          aria-label="Next quote"
          onClick={() => {
            go(1);
          }}
          className="text-marketing-text inline-flex size-9 items-center justify-center rounded-full bg-white transition-colors hover:bg-white/70"
        >
          <ArrowRightIcon className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
